import React, { useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function PremiumFloorSettings({ propId, lang, initialPct }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const start = initialPct == null ? 10 : Number(initialPct);
  const [pct, setPct] = useState(start);
  const [saving, setSaving] = useState(false);
  const dirty = Number(pct) !== start;

  const save = async () => {
    const v = Math.max(0, Math.min(40, Number(pct)));
    setSaving(true);
    try {
      await api.put(`/properties/${propId}/premium-floor`, { trim_pct: v });
      toast.success(L("Soglia salvata", "Threshold saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || L("Salvataggio non riuscito", "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-lg p-6" data-testid="mp-premium-floor-settings">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold">{L("Floor camere premium", "Premium-room floor")}</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {L(
          "Quando resta libera solo una camera premium (es. suite), l'AI non consiglia mai un prezzo sotto il minimo storico realmente incassato per quella camera in quel periodo. Per evitare che prezzi anomali e rari (upgrade gratuiti, errori) abbassino il minimo, scarto la percentuale di prezzi più bassi indicata qui sotto.",
          "When only a premium room (e.g. a suite) is left, the AI never suggests a price below the historical net minimum actually earned for that room in that period. To prevent rare, anomalous prices (free upgrades, data errors) from dragging the minimum down, I trim the share of lowest prices set below."
        )}
      </p>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
            {L("Taglio coda bassa (%)", "Low-tail trim (%)")}
          </label>
          <div className="flex items-center gap-2">
            <Input type="number" min={0} max={40} step={1} value={pct}
              onChange={(e) => setPct(e.target.value)}
              className="w-28 font-data text-lg" data-testid="mp-floor-trim-input" />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>
        <Button onClick={save} disabled={saving || !dirty} data-testid="mp-floor-trim-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
          {L("Salva", "Save")}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        {L(
          "Consigliato: 10% (scarta circa 1 prezzo su 10, i più bassi). 0% = nessun taglio. Il calcolo si applica solo alle camere con almeno 10 vendite in archivio.",
          "Recommended: 10% (trims about 1 price in 10, the lowest). 0% = no trim. Only applied to rooms with at least 10 recorded sales."
        )}
      </p>
    </motion.div>
  );
}
